import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const PerformanceGrid = ({ data = [], loading = false, onSort }) => {
  const [sortField, setSortField] = useState('emailsSent');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    
    if (onSort) {
      onSort(field, newDirection);
    }
  };

  const getSortedData = () => {
    if (!data || data?.length === 0) return [];
    
    const sorted = [...data]?.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];
      
      if (typeof aVal === 'string') aVal = parseFloat(aVal) || 0;
      if (typeof bVal === 'string') bVal = parseFloat(bVal) || 0;
      
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
    
    return sorted;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Activa', color: 'bg-green-100 text-green-700' },
      paused: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-700' },
      draft: { label: 'Borrador', color: 'bg-gray-100 text-gray-700' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`text-xs px-2 py-1 rounded ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const SortableHeader = ({ field, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-smooth"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field && (
          <Icon 
            name={sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
            size={14} 
            className="text-primary"
          />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
        <div className="flex justify-center items-center h-64">
          <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || data?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <Icon name="Database" size={48} className="text-muted-foreground mb-2" />
          <p className="text-sm caption text-muted-foreground">
            No hay datos de rendimiento disponibles
          </p>
        </div>
      </div>
    );
  }

  const sortedData = getSortedData();

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Table" size={24} className="text-primary" />
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Rendimiento de Reglas de Automatización
            </h2>
          </div>
          <span className="text-xs text-muted-foreground">
            {sortedData?.length} {sortedData?.length === 1 ? 'regla' : 'reglas'}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Regla
              </th>
              <SortableHeader field="emailsSent" label="Emails Enviados" />
              <SortableHeader field="openRate" label="Tasa de Apertura" />
              <SortableHeader field="clickRate" label="Tasa de Clics" />
              <SortableHeader field="conversionRate" label="Conversión" />
              <SortableHeader field="totalExecutions" label="Ejecuciones" />
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Estado
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedData?.map((rule) => (
              <tr key={rule?.ruleId} className="hover:bg-muted/20 transition-smooth">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {rule?.ruleName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {rule?.templateName} • {rule?.segment}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground font-medium">
                    {rule?.emailsSent?.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{rule?.openRate}%</span>
                    {parseFloat(rule?.openRate) > 40 && (
                      <Icon name="TrendingUp" size={14} className="text-success" />
                    )}
                    {parseFloat(rule?.openRate) < 20 && (
                      <Icon name="TrendingDown" size={14} className="text-error" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{rule?.clickRate}%</span>
                    {parseFloat(rule?.clickRate) > 15 && (
                      <Icon name="TrendingUp" size={14} className="text-success" />
                    )}
                    {parseFloat(rule?.clickRate) < 5 && (
                      <Icon name="TrendingDown" size={14} className="text-error" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{rule?.conversionRate}%</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{rule?.totalExecutions}</span>
                    <span className="text-xs text-muted-foreground">
                      {rule?.successRate?.toFixed(0)}% éxito
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(rule?.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceGrid;